import React from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    ScrollView,
} from 'react-native';
import Alert from "@/lib/Alert";
import {useTranslation} from 'react-i18next';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {useLogout} from "@/auth/useLogout";
import {ThemeType} from "@/theming/baseTheme";

// Main No Organization Page Component
export default function NoOrgAvailable() {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const styles = createStyles(theme);
    const { logout } = useLogout();

    const handleLogout = async () => {
        Alert.alert(
            t('auth.logout.confirmTitle'),
            t('auth.logout.confirmMessage'),
            [
                { text: t('common.cancel'), style: 'cancel' },
                {
                    text: t('auth.logout.button'),
                    style: 'destructive',
                    onPress: async () => {
                        try {
                            await logout();
                        } catch (error) {
                            console.error('Logout failed:', error);
                            Alert.alert(t('common.error'), t('auth.logout.error'));
                        }
                    }
                }
            ]
        );
    };

    return (
        <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
            {/* Main Content */}
            <View style={styles.content}>
                {/* Icon */}
                <View style={styles.iconContainer}>
                    <Text style={styles.icon}>🏢</Text>
                </View>

                {/* Title */}
                <Text style={styles.title}>{t('noOrg.title')}</Text>

                {/* Description */}
                <Text style={styles.description}>
                    {t('noOrg.description')}
                </Text>

                {/* Information Box */}
                <View style={styles.infoBox}>
                    <Text style={styles.infoTitle}>{t('noOrg.actions.title')}</Text>
                    <View style={styles.infoList}>
                        <Text style={styles.infoItem}>• {t('noOrg.actions.contactAdmin')}</Text>
                        <Text style={styles.infoItem}>• {t('noOrg.actions.askInvitation')}</Text>
                        <Text style={styles.infoItem}>• {t('noOrg.actions.checkAccount')}</Text>
                        <Text style={styles.infoItem}>• {t('noOrg.actions.tryRelogin')}</Text>
                    </View>
                </View>
            </View>

            {/* Actions */}
            <View style={styles.actions}>
                <TouchableOpacity
                    style={styles.logoutButton}
                    onPress={handleLogout}
                >
                    <Text style={styles.logoutButtonText}>{t('auth.logout.button')}</Text>
                </TouchableOpacity>
            </View>
        </ScrollView>
    );
}

// Styles
const createStyles = (theme: ThemeType) => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    contentContainer: {
        flex: 1,
        justifyContent: 'center',
        padding: theme.spacing.lg,
    },
    content: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: theme.spacing.xl,
    },
    iconContainer: {
        marginBottom: theme.spacing.xl,
    },
    icon: {
        fontSize: 80,
        textAlign: 'center',
        opacity: 0.6,
    },
    title: {
        fontSize: theme.fontSize.xxl,
        fontWeight: 'bold',
        color: theme.colors.text,
        textAlign: 'center',
        marginBottom: theme.spacing.md,
    },
    description: {
        fontSize: theme.fontSize.md,
        color: theme.colors.textSecondary,
        textAlign: 'center',
        lineHeight: 24,
        marginBottom: theme.spacing.xl,
        maxWidth: 300,
    },
    infoBox: {
        backgroundColor: theme.colors.surface,
        padding: theme.spacing.lg,
        borderRadius: theme.borderRadius.md,
        borderWidth: 1,
        borderColor: theme.colors.border,
        width: '100%',
        maxWidth: 350,
    },
    infoTitle: {
        fontSize: theme.fontSize.lg,
        fontWeight: '600',
        color: theme.colors.text,
        marginBottom: theme.spacing.md,
    },
    infoList: {
        gap: theme.spacing.sm,
    },
    infoItem: {
        fontSize: theme.fontSize.sm,
        color: theme.colors.textSecondary,
        lineHeight: 20,
    },
    actions: {
        paddingTop: theme.spacing.xl,
        width: '100%',
        alignItems: 'center',
    },
    logoutButton: {
        backgroundColor: theme.colors.error,
        paddingVertical: theme.spacing.md,
        paddingHorizontal: theme.spacing.xl,
        borderRadius: theme.borderRadius.md,
        minWidth: 120,
        alignItems: 'center',
    },
    logoutButtonText: {
        color: '#ffffff',
        fontSize: theme.fontSize.md,
        fontWeight: '600',
    },
});
